import React, { useState } from 'react';
import { 
  Settings, 
  DollarSign, 
  AlertTriangle, 
  Bell, 
  Link2, 
  CheckCircle2,
  XCircle,
  Shield, 
  Code, 
  Workflow,
  Edit,
  Save,
  RefreshCw
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface FeeStructure {
  id: string;
  transactionType: string;
  feeType: 'percentage' | 'fixed';
  rate: number;
  minimumFee?: number;
  maximumFee?: number;
}

interface TransactionLimit {
  id: string;
  transactionType: string;
  dailyLimit: number;
  monthlyLimit: number;
  maxSingleTransactionAmount: number;
}

interface RiskParameter {
  id: string;
  name: string;
  type: 'transaction' | 'account' | 'global';
  threshold: number;
  action: 'flag' | 'block' | 'review';
}

interface NotificationSetting {
  id: string;
  event: string;
  email: boolean;
  sms: boolean;
  inApp: boolean;
}

interface WorkflowConfiguration {
  id: string;
  name: string;
  steps: string[];
  approvalRequired: boolean;
  requiredApprovals: number;
}

interface APIIntegration {
  id: string;
  name: string;
  apiKey: string;
  status: 'active' | 'inactive';
  lastSync?: string;
}

const SystemConfiguration: React.FC = () => {
      useBreadcrumbs([
            { label: 'Escrow Management', href: '#' },
            { label: 'Escrow System Configuration' }
          ]);
  // State Management
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([
    {
      id: 'FEE001',
      transactionType: 'Withdrawal',
      feeType: 'percentage',
      rate: 2.5,
      minimumFee: 1,
      maximumFee: 50
    }
  ]);

  const [transactionLimits, setTransactionLimits] = useState<TransactionLimit[]>([
    {
      id: 'LIMIT001',
      transactionType: 'Peer Transfer',
      dailyLimit: 5000,
      monthlyLimit: 50000,
      maxSingleTransactionAmount: 10000
    }
  ]);

  const [riskParameters, setRiskParameters] = useState<RiskParameter[]>([
    {
      id: 'RISK001',
      name: 'High Value Transaction',
      type: 'transaction',
      threshold: 10000,
      action: 'review'
    }
  ]);

  const [notificationSettings, setNotificationSettings] = useState<NotificationSetting[]>([
    {
      id: 'NOTIF001',
      event: 'Transaction Completed',
      email: true,
      sms: false,
      inApp: true
    }
  ]);

  const [workflowConfigurations, setWorkflowConfigurations] = useState<WorkflowConfiguration[]>([
    {
      id: 'WORKFLOW001',
      name: 'High Value Transaction Approval',
      steps: ['Initial Check', 'Manager Approval', 'Compliance Review'],
      approvalRequired: true,
      requiredApprovals: 2
    }
  ]);

  const [apiIntegrations, setAPIIntegrations] = useState<APIIntegration[]>([
    {
      id: 'API001',
      name: 'Payment Gateway',
      apiKey: 'sk_test_abc123xyz',
      status: 'active',
      lastSync: '2024-02-20 10:30:45'
    }
  ]);

  // Render Methods for Each Configuration Section
  const renderFeeStructures = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <DollarSign className="mr-2 h-5 w-5 text-blue-500" /> 
            Fee Structures
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Edit className="mr-1 h-4 w-4" /> Modify
          </button>
        </div>
        <div className="space-y-4">
          {feeStructures.map((fee) => (
            <div 
              key={fee.id} 
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">{fee.transactionType}</h3>
                  <p className="text-sm text-gray-500">
                    {fee.feeType === 'percentage' 
                      ? `${fee.rate}% (Min: ${fee.minimumFee}, Max: ${fee.maximumFee})` 
                      : `Fixed Fee: ${fee.rate}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTransactionLimits = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-red-500" /> 
            Transaction Limits
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Edit className="mr-1 h-4 w-4" /> Modify
          </button>
        </div>
        <div className="space-y-4">
          {transactionLimits.map((limit) => (
            <div 
              key={limit.id} 
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">{limit.transactionType}</h3>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-gray-500">Daily Limit</p>
                      <p className="text-sm font-semibold">
                        {limit.dailyLimit.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Monthly Limit</p>
                      <p className="text-sm font-semibold">
                        {limit.monthlyLimit.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Max Single Transaction</p>
                      <p className="text-sm font-semibold">
                        {limit.maxSingleTransactionAmount.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRiskParameters = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shield className="mr-2 h-5 w-5 text-green-500" /> 
            Risk Parameters
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Edit className="mr-1 h-4 w-4" /> Modify
          </button>
        </div>
        <div className="space-y-4">
          {riskParameters.map((risk) => (
            <div 
              key={risk.id} 
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">{risk.name}</h3>
                  <p className="text-sm text-gray-500 capitalize">
                    Type: {risk.type} | Threshold: {risk.threshold} | 
                    Action: {risk.action}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderNotificationSettings = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Bell className="mr-2 h-5 w-5 text-purple-500" /> 
            Notification Settings
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Edit className="mr-1 h-4 w-4" /> Modify
          </button>
        </div>
        <div className="space-y-4">
          {notificationSettings.map((setting) => (
            <div 
              key={setting.id} 
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">{setting.event}</h3>
                  <div className="flex space-x-4 mt-2">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">Email</span>
                      {setting.email ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">SMS</span>
                      {setting.sms ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">In-App</span>
                      {setting.inApp ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Settings className="mr-3 h-8 w-8 text-blue-600" />
            System Configuration
          </h1>

          {/* Configuration Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Fee Structures and Transaction Limits */}
            <div>
              {renderFeeStructures()}
            </div>
            <div>
              {renderTransactionLimits()}
            </div>
          </div>

          {/* Risk and Notification Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              {renderRiskParameters()}
            </div>
            <div>
              {renderNotificationSettings()}
            </div>
          </div>

          {/* TODO: Add Workflow Configurations and API Management sections */}
        </div>
      </div>
    </div>
  );
};

export default SystemConfiguration;